/**
 * Shared utilities for expression handling in DatePicker and DateRangePicker
 */
import { isAbsoluteDate, resolveExpression, formatDateTimeToYMDHMS, parse } from "./parser";

/**
 * Check if a single value contains a relative expression (not an absolute date)
 */
export function isExpression(value: string | null | undefined): boolean {
  return Boolean(value && !isAbsoluteDate(value));
}

/**
 * Check if any of the provided values contain expressions
 */
export function hasExpressions(...values: (string | null | undefined)[]): boolean {
  return values.some(isExpression);
}

/**
 * Count the number of expressions in the provided values
 */
export function countExpressions(...values: (string | null | undefined)[]): number {
  return values.filter(isExpression).length;
}

/**
 * Resolve a single expression value to a fixed ISO date string
 * Returns null if the value is already absolute or if resolution fails
 *
 * Uses forDisplay: true to get the date in display format (local-as-UTC for local timezone),
 * then formats it as an ISO string for storage.
 */
export function resolveToFixedDate(
  value: string | null | undefined,
  timezone: string
): string | null {
  if (!value || isAbsoluteDate(value)) return null;

  // Use forDisplay: true to get date in the picker's storage format
  const resolved = resolveExpression(value, timezone, { forDisplay: true });
  if (!resolved) return null;

  return `${formatDateTimeToYMDHMS(resolved)}Z`;
}

/**
 * Apply expression resolution to a value - returns the ISO string if it was an expression,
 * otherwise returns null (no change needed)
 */
export function applyExpression(
  value: string | null | undefined,
  timezone: string,
  setValue: (v: string) => void
): boolean {
  const resolved = resolveToFixedDate(value, timezone);
  if (resolved) {
    setValue(resolved);
    return true;
  }
  return false;
}

/**
 * Resolve a pair of from/to expressions, handling cross-references (start/end anchors).
 *
 * When fromValue contains "end" reference, it uses toValue as the base date.
 * When toValue contains "start" reference, it uses fromValue as the base date.
 *
 * @param fromValue - The from/start date expression (may reference "end")
 * @param toValue - The to/end date expression (may reference "start")
 * @param timezone - Timezone for resolution
 * @returns Object with resolved from and to dates (null if resolution fails)
 */
export function resolveExpressionPair(
  fromValue: string | null | undefined,
  toValue: string | null | undefined,
  timezone: string
): { from: Date | null; to: Date | null } {
  const fromLower = (fromValue ?? "").toLowerCase().trim();
  const toLower = (toValue ?? "").toLowerCase().trim();
  const fromReferencesEnd = fromLower.startsWith("end");
  const toReferencesStart = toLower.startsWith("start");

  let fromDate: Date | null = null;
  let toDate: Date | null = null;

  if (fromReferencesEnd && toValue) {
    // Resolve toValue first, then use it as baseDate for fromValue
    toDate = resolveExpression(toValue, timezone);
    if (toDate && fromValue) {
      const fromParsed = parse(fromValue, { pickerType: "single", timezone, baseDate: toDate });
      fromDate = fromParsed.resolved ?? null;
    }
  } else if (toReferencesStart && fromValue) {
    // Resolve fromValue first, then use it as baseDate for toValue
    fromDate = resolveExpression(fromValue, timezone);
    if (fromDate && toValue) {
      const toParsed = parse(toValue, { pickerType: "single", timezone, baseDate: fromDate });
      toDate = toParsed.resolved ?? null;
    }
  } else {
    // No cross-references, resolve normally
    fromDate = fromValue ? resolveExpression(fromValue, timezone) : null;
    toDate = toValue ? resolveExpression(toValue, timezone) : null;
  }

  return { from: fromDate, to: toDate };
}
